import classnames from 'classnames';
import {getCDNBaseURL} from 'components/atoms/Media/AsyncImage/helper';
import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {LazyLoadImage} from 'react-lazy-load-image-component';
import {getCurrentSiteId, getEnvSpecificUrl} from 'features/services/utils/env-helper';

import './style.scss';
import {EpiImage} from "features/search/types";

const isStorybook = (import.meta as unknown as { env: Record<string, string> }).env.STORYBOOK === 'true';

export interface Dimensions {
    width: string;
    height: string;
}

interface Props  {
    image?: EpiImage;
    src?: string;
    alt?: string;
    onError?: (errorEvent: Event) => void;
    quality?: number;
    className?: string;
    onLoad?: () => void;
    getImageDimensions?: (dimensions: Dimensions) => void;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    radius?: boolean;
    fullHeight?: boolean;
    fullWidth?: boolean;
    isVideo?: boolean;
    hideCanvas?: boolean;
    disableScrollDetect?: boolean;
    children?: React.ReactNode;
    skipMobileQualityCheck?: boolean;
    headerImage?: boolean;
    style?: React.CSSProperties;
    noCrop?: boolean;
    forceVisibility?: boolean;
}

export const AsyncImage: React.FunctionComponent<Props> = ({image, src, alt: _alt, onError: _onError, quality, className, onLoad: _onLoad, getImageDimensions: _getImageDimensions, onClick, radius, fullHeight, forceVisibility, fullWidth, isVideo, hideCanvas, disableScrollDetect: _disableScrollDetect,  children: _children,  skipMobileQualityCheck: _skipMobileQualityCheck, headerImage, style, noCrop}) => {

    const [standingImage, setStandingImage] = useState<boolean>(false);
    const [ratioPrecentage, setRatioPrecentage] = useState<number>(0);
    const [isInSlider, setIsInSlider] = useState<boolean>(forceVisibility ? true : false);
    const imageRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if(imageRef.current !== null) {
            const sliderParent = imageRef.current.closest('.slick-list');
            const scrollParent = imageRef.current.closest('.text-and-image-block__mobile-image__scroll');

            if(sliderParent !== null || scrollParent !== null) {
                setIsInSlider(true);
            }
        }
    }, [imageRef]);

    const getImageSrc = () => {
        if (image) {
            return image.url;
        }

        if (src) {
            return src;
        }

        return '';
    };

    const getImageAlt = () => {
        if (image) {
            if (typeof image !== 'undefined') {
                if (typeof image.altText !== 'undefined' ) {
                    if (image.altText !== null) {
                        return image.altText.toString();
                    }
                }
                if (typeof image.description !== 'undefined' ) {
                    if (image.description !== null) {
                        return image.description.toString();
                    }
                }
            }
        }

        /*if (alt) {
            return alt;
        }*/

        return undefined;
    };

    const getSrcSet = () => {
        if (!headerImage) return '';
        const image = getImageSrc();

        if (image.indexOf('.gif') > -1) {
            return `
            ${image} 1200w,
            ${image} 1900w,
            ${image} 2560w
        `;
        }

        return `
            ${getImageFromCDN(1200)} 1200w,
            ${getImageFromCDN(1900)} 1900w,
            ${getImageFromCDN(2560)} 2560w
        `;
    };

    const getSizes = () => {
        if (!headerImage) return '';

        return `(min-width: 1920px) 2560px, (min-width: 1200px) 1900px, (min-width: 600px) 1200px`;
    };


    const setRatio = (e) => {
        if(!e.target) return;

        let internalRationPercentage;
        let standingImage = false;

        if(image && image.orgHeight && image.orgWidth) {

            if(image.orgHeight > image.orgWidth) {
                standingImage = true;
            }

            if(noCrop) {
                internalRationPercentage = image.orgHeight / image.orgWidth * 100;
            } else {
                internalRationPercentage = standingImage ? 3/2 * 100 : 2/3 * 100;
            }


            setRatioPrecentage(internalRationPercentage);
            setStandingImage(standingImage);

            return;
        }

        if (e.target.height > e.target.width) {
            standingImage = true;
        }

        if(noCrop) {
            internalRationPercentage = e.target.height / e.target.width * 100;
        } else {
            internalRationPercentage = standingImage ? 3/2 * 100 : 2/3 * 100;
        }

        setRatioPrecentage(internalRationPercentage);
        setStandingImage(standingImage);
    };


    const getImageFromCDN = (internalQuality?: number) => {

        let url = getCDNBaseURL();

        const src = getImageSrc();
        if(!src || !internalQuality) return '';

        const baseUrl =  (src.indexOf('http://') === 0 || src.indexOf('https://') === 0) ? '' : getEnvSpecificUrl(getCurrentSiteId(), true);


        if(standingImage) {
            url += `width=${Math.round(internalQuality/3*2)},height=${internalQuality},quality=75`;
        } else {
            if(noCrop) {
                url += `width=${internalQuality},height=${Math.round(internalQuality*(ratioPrecentage/100))},quality=75`;
            } else {
                url += `width=${internalQuality},height=${Math.round(internalQuality/3*2)},quality=75`;
            }
        }

        if(!noCrop) {
            url += ',fit=crop';
        }

        url += `,format=webp/${baseUrl}${src}`;

        return url;
    };


    const ratioClasses = classnames('async-image__aspect-holder', {
        'async-image__aspect-holder--video': isVideo,
    });

    const imageClasses = classnames('async-image', className, {
        'async-image--standing': standingImage,
        'async-image--full-height': fullHeight,
        'async-image--full-width': fullWidth,
        'async-image--circle': radius,
        'async-image--hide-canvas': hideCanvas,
    });

    // new image component

    // Reflect image orientation so standing (portrait) images look portrait in Storybook too.
    const getStorybookDimensions = () => {
        const q = quality || 800;
        const standing = !!(image?.orgHeight && image?.orgWidth && image.orgHeight > image.orgWidth);
        return standing
            ? { w: Math.round(q * 2 / 3), h: q }
            : { w: q, h: Math.round(q * 2 / 3) };
    };

    // Seeded Picsum photo so Storybook shows a realistic image instead of a flat placeholder.
    // Absolute URLs in the fixture are used as-is; the seed is derived from the filename so the
    // same image stays stable across reloads.
    const getStorybookImage = () => {
        const rawSrc = getImageSrc();
        if (/^https?:\/\//i.test(rawSrc)) return rawSrc;
        const { w, h } = getStorybookDimensions();
        const seed = encodeURIComponent((rawSrc.split('/').pop() || 'jm').replace(/\.[^.]+$/, '') || 'jm');
        return `https://picsum.photos/seed/${seed}/${w}/${h}`;
    };

    // Offline/blocked Picsum loads fall back to an SVG placeholder via a global error handler in
    // .storybook/preview.tsx, so the fallback logic stays out of this production component.
    const internalSrc = isStorybook ? getStorybookImage() : getImageFromCDN(quality);

    return (
        <figure className={imageClasses} style={style} ref={imageRef}>
            <div className="async-image__ratio-holder">
                <div className={ratioClasses} style={ratioPrecentage ? { paddingTop: `${ratioPrecentage}%` } : {}} />
                <div className="async-image__media-holder">
                    { internalSrc &&
                        <LazyLoadImage
                            alt={getImageAlt()}
                            effect={'opacity'}
                            className="async-image__media image-loaded"
                            src={internalSrc}
                            srcSet={isStorybook ? '' : getSrcSet()}
                            sizes={getSizes()}
                            onLoad={setRatio}
                            onClick={onClick && onClick}
                            visibleByDefault={isInSlider}
                        />
                    }
                </div>


            </div>
        </figure>

    );
};
